/* eslint-disable max-lines */
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { WidgetControllerContext } from "@swiggy-private/dls-react-mxn";
import ElementObserver from "@swiggy-private/dls-react-mxn/dist/ElementObserver";
import { GridContainer } from "@swiggy-private/dls-web-core/dist/components/GridContainer";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import {
    SwiggyContextHeader,
    useApiContext,
    ObjectNames,
    ResponseContext,
    ScreenNames,
    TableContext,
} from "@swiggy-private/portal-dineout-context";
import { ACTION_TYPE, DEFAULT_CATEGORY_IMAGE, IMAGE_DIMENSION, PADDING_LEFT } from "./constants";
import {
    createCategoryAnalyticsContext,
    getHPosVPosOfCategory,
    getScrollPosition,
    isElementFullyVisible,
    truncateName,
} from "./helpers";
import { Container, ImageContainer, GridWrapper, Separator, StickContainer, Title } from "./styled";
import { IMenuCategoryInfo, IMenuCategoryGrid } from "./types";
import useElementHeight from "../../../hooks/useElementHeight";
import { getRestaurantName, isCurrentUserHost } from "../../../utils";
import {
    ITEMS_FOR_SINGLE_LINE,
    SCROLL_POSITION_WITH_CATEGORY,
    SCROLL_POSITION_WITH_MULTIPLE_LINES,
    SCROLL_TIMEOUT,
} from "../../constants";
import { scrollToTop } from "../../helper";
import { CustomWidgetTypes } from "../../types";

const MenuCategoryGrid = ({
    categoryList,
    currentSelectedCategoryId,
}: IMenuCategoryGrid): JSX.Element => {
    const selectedRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const nonStickyContainerRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const loggedCategoryIdsRef = useRef<Set<string>>(new Set());
    const { clickHandler, impressionAnalytics, clickAnalytics } =
        useContext(WidgetControllerContext);
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { sendEvent } = useApiContext();
    const { state } = useContext(ResponseContext);
    const { tableId = "", restaurantId = "" } = useContext(TableContext);
    const isUserHost = useMemo(
        () => isCurrentUserHost(state?.pin?.data?.status ?? ""),
        [state?.pin?.data?.status]
    );
    const [isScrolled, setIsScrolled] = useState(false);
    const [categoryItemCount, setCategoryItemCount] = useState<number | null>(null);
    const restaurantName = useMemo(
        () => getRestaurantName(state?.menu?.filteredData ?? []),
        [state?.menu?.filteredData]
    );

    const handleCategoryClickAnalytics = useCallback(
        ({
            categoryId,
            name,
            index,
            total,
            parentType,
        }: {
            categoryId: string | number;
            name: string;
            index: number;
            total: number;
            parentType: string;
        }) => {
            if (isEmpty(clickAnalytics)) {
                return;
            }

            const { hpos, vpos } = getHPosVPosOfCategory(index, total);

            clickAnalytics({
                category: ScreenNames.MENU,
                action: ObjectNames.CLICK_ITEM_CATEGORY,
                label: name,
                value: "-",
                context: createCategoryAnalyticsContext({
                    name,
                    categoryId,
                    tableNo: state.pin.data?.tableNo,
                    tableId: tableId || "",
                    pin: state.pin.data?.pin,
                    restaurantId: restaurantId || "",
                    isUserHost,
                    parentType,
                }),
                extraParams: JSON.stringify({
                    hpos,
                    vpos,
                    restaurant_name: restaurantName,
                }),
                source: CustomWidgetTypes.MENU_CATEGORY_GRID,
            });
        },
        [
            clickAnalytics,
            state.pin.data.tableNo,
            state.pin.data.pin,
            tableId,
            restaurantId,
            isUserHost,
            restaurantName,
        ]
    );

    const handleCategoryClick = useCallback(
        ({
            categoryId,
            count,
            name,
            index,
            total,
            parentType,
        }: {
            categoryId: string | number;
            count: number;
            name: string;
            index: number;
            total: number;
            parentType: string;
        }) => {
            if (isEmpty(clickHandler)) {
                return;
            }

            handleCategoryClickAnalytics({ categoryId, name, index, total, parentType });

            clickHandler(undefined, { categoryId, action: ACTION_TYPE });
            setCategoryItemCount(count);
        },
        [clickHandler, setCategoryItemCount, handleCategoryClickAnalytics]
    );

    const handleScroll = useCallback(() => {
        if (isEmpty(clickHandler)) {
            return;
        }

        const scrollParams = {
            selectedRef,
            containerRef: isScrolled ? nonStickyContainerRef : containerRef,
            categoryList,
            categoryId: currentSelectedCategoryId?.toString() || "",
            PADDING_LEFT,
        };

        if (!isScrolled) {
            // Handle sticky container scrolling
            const scrollResult = getScrollPosition(scrollParams);

            if (scrollResult && !scrollResult.isFullyVisible && containerRef.current) {
                containerRef.current.scrollTo({
                    left: scrollResult.scrollPosition,
                    behavior: "smooth",
                });
            }
        } else {
            // Handle non-sticky container scrolling
            if (!isElementFullyVisible(scrollParams) && selectedRef.current) {
                selectedRef.current.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "nearest",
                });
            }
        }
    }, [clickHandler, isScrolled, categoryList, currentSelectedCategoryId]);

    const onCategoryImpression = useCallback(
        ({
            categoryId,
            name,
            index,
            total,
            parentType,
        }: {
            categoryId: string | number;
            name: string;
            index: number;
            total: number;
            parentType: string;
        }) => {
            if (isEmpty(impressionAnalytics)) return;
            const { hpos, vpos } = getHPosVPosOfCategory(index, total);

            impressionAnalytics({
                category: ScreenNames.MENU,
                action: ObjectNames.IMPRESSION_ITEM_CATEGORY,
                label: name,
                value: "-",
                context: createCategoryAnalyticsContext({
                    name,
                    categoryId,
                    tableNo: state.pin.data?.tableNo,
                    tableId: tableId || "",
                    pin: state.pin.data?.pin,
                    restaurantId: restaurantId || "",
                    isUserHost,
                    parentType,
                }),
                extraParams: JSON.stringify({
                    hpos,
                    vpos,
                    restaurant_name: restaurantName,
                }),
            });
        },
        [
            impressionAnalytics,
            state.pin.data.tableNo,
            state.pin.data.pin,
            tableId,
            restaurantId,
            isUserHost,
            restaurantName,
        ]
    );

    useEffect(() => {
        if (isEmpty(clickHandler)) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(handleScroll, SCROLL_TIMEOUT);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [handleScroll, clickHandler]);

    useEffect(() => {
        if (currentSelectedCategoryId && categoryItemCount) {
            scrollTimeoutRef.current = setTimeout(() => {
                scrollToTop(
                    categoryList?.length >= ITEMS_FOR_SINGLE_LINE
                        ? SCROLL_POSITION_WITH_MULTIPLE_LINES
                        : SCROLL_POSITION_WITH_CATEGORY
                );
            }, SCROLL_TIMEOUT);
        }

        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = undefined;
            }
        };
    }, [currentSelectedCategoryId, categoryItemCount, categoryList]);

    const imageContainerDimension =
        typeof window !== "undefined"
            ? IMAGE_DIMENSION * (window.innerWidth / 350)
            : IMAGE_DIMENSION;

    const gridItemWidth = imageContainerDimension + PADDING_LEFT;

    useEffect(() => {
        isMountedRef.current = true;
    }, []);

    const logImageError = useCallback(
        ({
            type,
            logo,
            name,
            categoryId,
        }: {
            type: string;
            logo?: string;
            name?: string;
            categoryId?: string;
        }) => {
            sendEvent?.(
                type,
                {
                    logo: logo || "-",
                    name: name || "-",
                    categoryId: categoryId || "-",
                },
                swiggyHeaders || {}
            );
        },
        [sendEvent, swiggyHeaders]
    );

    const formatRows = useCallback(
        (cards: IMenuCategoryInfo[]) => {
            if (!cards || cards.length === 0) return [];

            const rows: { columns: JSX.Element[] }[] = [];
            const row1: JSX.Element[] = [];
            const row2: JSX.Element[] = [];
            const total = cards.length;

            cards?.forEach(({ logo, name, count, categoryId, parentType }, index) => {
                if (!name || !count) return;

                if (isEmpty(logo?.url)) {
                    const categoryIdStr = String(categoryId);
                    if (!loggedCategoryIdsRef.current.has(categoryIdStr)) {
                        loggedCategoryIdsRef.current.add(categoryIdStr);
                        logImageError({
                            type: "category_image_empty",
                            logo: logo?.url,
                            name,
                            categoryId: categoryIdStr,
                        });
                    }
                }

                const cardComponent = (
                    <ElementObserver
                        key={index}
                        onViewEnter={() =>
                            onCategoryImpression({ categoryId, name, index, total, parentType })
                        }
                        senseOnce
                    >
                        <Container
                            onClick={() =>
                                handleCategoryClick({
                                    categoryId: categoryId || "",
                                    count,
                                    name,
                                    index,
                                    total,
                                    parentType,
                                })
                            }
                        >
                            <ImageContainer
                                selected={categoryId === currentSelectedCategoryId}
                                height={imageContainerDimension}
                                width={imageContainerDimension}
                                ref={categoryId === currentSelectedCategoryId ? selectedRef : null}
                            >
                                <Image
                                    cloudinaryId={logo?.url || DEFAULT_CATEGORY_IMAGE}
                                    cloudinaryHeight={Math.abs(imageContainerDimension) * 2}
                                    cloudinaryWidth={Math.abs(imageContainerDimension) * 2}
                                    fit="fill"
                                    alt=""
                                    background="text_Highest_Emphasis"
                                    onError={() => {
                                        logImageError({
                                            type: "category_image_error",
                                            logo: logo?.url,
                                            name,
                                            categoryId: String(categoryId),
                                        });
                                    }}
                                />
                            </ImageContainer>
                            <Title
                                variant="V2.overline.neutral.medium"
                                color={
                                    categoryId === currentSelectedCategoryId
                                        ? "text_Highest_Emphasis"
                                        : "text_Med_Emphasis"
                                }
                                gridItemWidth={imageContainerDimension + PADDING_LEFT / 2}
                            >
                                {`${truncateName(name, count, imageContainerDimension)}`}
                            </Title>
                        </Container>
                    </ElementObserver>
                );

                if (total <= ITEMS_FOR_SINGLE_LINE) {
                    // For ITEMS_FOR_SINGLE_LINE i.e. 8 or fewer items, use a single row.
                    row1.push(cardComponent);
                } else {
                    // For 9 or more items:
                    // Row1 gets the first Math.ceil(total/2) items,
                    // Row2 gets the remaining.
                    if (index < Math.ceil(total / 2)) {
                        row1.push(cardComponent);
                    } else {
                        row2.push(cardComponent);
                    }
                }
            });

            if (row1.length) rows.push({ columns: row1 });
            if (row2.length) rows.push({ columns: row2 });

            return rows;
        },
        [
            currentSelectedCategoryId,
            handleCategoryClick,
            imageContainerDimension,
            onCategoryImpression,
            logImageError,
        ]
    );

    const rows = useMemo(() => {
        return formatRows(categoryList);
    }, [categoryList, formatRows]);

    // Measure the CONTENT (not the wrapper we apply `height` to) to avoid feedback loops
    const categoryListLength = categoryList?.length ?? 0;
    const gridHeightWatchKey = useMemo(
        () => `${rows.length}-${categoryListLength}`,
        [rows.length, categoryListLength]
    );
    const { ref: gridContentRef, height: gridHeight } =
        useElementHeight<HTMLDivElement>(gridHeightWatchKey);

    // merge in single row for scrolled state
    const singleRow = [
        { columns: rows.reduce((acc, row) => acc.concat(row.columns), [] as JSX.Element[]) },
    ];

    if (isEmpty(categoryList)) {
        return <></>;
    }

    return (
        <>
            <ElementObserver
                onViewEnter={() => setIsScrolled(true)}
                onViewExit={() => setIsScrolled(false)}
            >
                <div />
            </ElementObserver>

            <StickContainer isSticky={true} isVisible={!isScrolled && isMountedRef.current}>
                <GridContainer
                    items={{
                        rows: singleRow,
                    }}
                    title=""
                    interItemSpacing={"empty"}
                    itemWidth={`${gridItemWidth}px`}
                    background="none"
                    preventHardcodedThemeWithCustomBackground
                    hasCustomSpacing
                    clearTitleDefaultMargin
                    containerPadding={{
                        top: "16",
                        bottom: "4",
                    }}
                    widgetPadding={{
                        left: PADDING_LEFT,
                        bottom: "12",
                        top: "4",
                    }}
                    scrollableContainerRef={containerRef}
                />
            </StickContainer>

            <StickContainer isSticky={false} isVisible={true}>
                <GridWrapper height={gridHeight}>
                    <div ref={gridContentRef}>
                        <GridContainer
                            items={{
                                rows: rows,
                            }}
                            title=""
                            interItemSpacing={"empty"}
                            itemWidth={`${gridItemWidth}px`}
                            background="none"
                            preventHardcodedThemeWithCustomBackground
                            hasCustomSpacing
                            clearTitleDefaultMargin
                            containerPadding={{
                                top: "16",
                                bottom: "4",
                            }}
                            widgetPadding={{
                                bottom: "20",
                                top: "4",
                                left: PADDING_LEFT,
                            }}
                            scrollableContainerRef={nonStickyContainerRef}
                        />
                    </div>
                </GridWrapper>
            </StickContainer>
            <Separator />
        </>
    );
};

export default memo(MenuCategoryGrid);
